import { useCallback, useEffect, useRef, useState } from 'react';

interface UseActiveBarPositionProps {
  currentActiveValue: string | number;
  data: Array<{ value: string | number }>;
}

export const useActiveBarPosition = ({ currentActiveValue, data }: UseActiveBarPositionProps) => {
  const tabRefs = useRef<Record<string | number, HTMLDivElement | null>>({});
  const [activeBarStyle, setActiveBarStyle] = useState<{
    width: number;
    transform: string;
  }>({
    width: 0,
    transform: 'translateX(0px)',
  });

  const calculateActiveBarPosition = useCallback(() => {
    const activeTabElement = tabRefs.current[currentActiveValue];
    if (!activeTabElement) return;

    const tabsList = activeTabElement.parentElement;
    if (!tabsList) return;

    const parentComputedStyle = window.getComputedStyle(tabsList);
    const paddingLeft = parseInt(parentComputedStyle.paddingLeft, 10) || 0;

    const offsetLeft = activeTabElement.offsetLeft - paddingLeft;
    const width = activeTabElement.offsetWidth;

    setActiveBarStyle({
      width,
      transform: `translateX(${offsetLeft}px)`,
    });
  }, [currentActiveValue]);

  useEffect(() => {
    const timer = setTimeout(() => {
      calculateActiveBarPosition();
    }, 50);

    return () => clearTimeout(timer);
  }, [currentActiveValue, calculateActiveBarPosition]);

  useEffect(() => {
    const handleResize = () => {
      calculateActiveBarPosition();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [calculateActiveBarPosition]);

  useEffect(() => {
    if (!window.ResizeObserver) return;

    const resizeObserver = new ResizeObserver(() => {
      calculateActiveBarPosition();
    });

    Object.values(tabRefs.current).forEach((tabElement) => {
      if (tabElement) {
        resizeObserver.observe(tabElement);
      }
    });

    return () => {
      resizeObserver.disconnect();
    };
  }, [data, calculateActiveBarPosition]);

  return {
    tabRefs,
    activeBarStyle,
  };
};